import { useState, useRef, useEffect, useContext } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import Loading from "../../Loading"
import ManualBarGraphWrapper from "../../graph/ManualBarGraphWrapper"
import DividendTable from "../components/DividendTable"
import TimeRangeContainer from "../../utils/TimeRangeContainer"
import AxisSettingButton from "../../utils/AxisSettingButton"
import { UserSettingsContext, getDescriptionBySettingName } from "../../../context/UserSettingsContext"
import SaveSettingButton from "../components/SaveSettingButton"
import { useDashboardContext } from "../../../context/DashboardContext"
import { fetchDividendData } from "../../api/api_calls"
import { currencyToSymbol } from "../../utils/commonUtils"
import { fetchCompanyInfoData } from "../../api/api_calls"

const Dividends = ({ secCode, companyInfo }) => {
  const { state: dashboardState, dispatch: dashboardDispatch } = useDashboardContext()
  const navigate = useNavigate()
  const location = useLocation()

  // クエリパラメータを取得
  const [searchParams] = useSearchParams()

  const { userSettings, addUserSetting } = useContext(UserSettingsContext)

  // year パラメータを state へ反映するための初期化
  const [selectedTimeRange, setSelectedTimeRange] = useState(() => {
    // URL に ?year=xxx が指定されていれば、それを優先
    const paramYear = searchParams.get("year")
    if (paramYear) {
      return paramYear
    }
    // なければ、ユーザー設定 or デフォルトを使う
    return getDescriptionBySettingName(userSettings, "dashboard_dividend_data_graph_timerange") || "5Y"
  })

  // ラベルの設定も同様にユーザー設定 or デフォルト
  const [labelState, setLabelState] = useState(() => {
    return getDescriptionBySettingName(userSettings, "dashboard_dividend_data_graph_label") || "auto"
  })

  const [dividendData, setDividendData] = useState(null)
  const [dividendCurrency, setDividendCurrency] = useState(null)
  const [width, setWidth] = useState(0)
  const [selectedRows, setSelectedRows] = useState(["dividend"])

  const ref = useRef(null)

  // 初期ロード時に会社情報を取得して通貨を設定
  useEffect(() => {
    // const getCompanyInfoData = async (secCode) => {
    // if (dashboardState.companyInfo) {
      if (companyInfo) {
        setDividendCurrency(companyInfo.currency !== null ? companyInfo.currency : "")
      }
    // getCompanyInfoData(secCode)
  }, [secCode, companyInfo])

  // 配当データを取得（またはコンテキストから取得）して state に格納
  useEffect(() => {
    const getDividendData = async () => {
      let divData
      if (dashboardState.dividendData) {
        divData = dashboardState.dividendData
      } else {
        const fetchedData = await fetchDividendData(secCode, true, true, navigate, location)
        divData = fetchedData.data
        dashboardDispatch({ type: "SET_DIVIDEND_DATA", payload: divData })
      }
      setDividendData(divData)
    }
    getDividendData()
  }, [secCode, dashboardState.dividendData, dashboardDispatch, navigate, location])

  // 親要素のリサイズを監視してグラフ幅を更新
  useEffect(() => {
    if (!ref.current) return
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          setWidth(entry.contentRect.width)
        }
      }
    })
    observer.observe(ref.current)

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [])

  // 指定された timeRange で配当データをフィルタリング
  const makeDisplayData = (data) => {
    if (!data || data.length === 0) return []

    let displayData
    const latestDate = new Date(data[data.length - 1].date)
    const startDate = new Date(latestDate)

    switch (selectedTimeRange) {
      case "3Y":
        startDate.setFullYear(latestDate.getFullYear() - 3)
        displayData = data.filter((item) => new Date(item.date) >= startDate)
        break
      case "5Y":
        startDate.setFullYear(latestDate.getFullYear() - 5)
        displayData = data.filter((item) => new Date(item.date) >= startDate)
        break
      case "10Y":
        startDate.setFullYear(latestDate.getFullYear() - 10)
        displayData = data.filter((item) => new Date(item.date) >= startDate)
        break
      case "MAX":
        displayData = data
        break
      default:
        displayData = data
        break
    }
    return displayData
  }

  // グラフ描画用データ生成
  const makeChartData = (filteredData) => {
    return selectedRows.map((row) => {
      const column = row === "dividend" ? "adjdividend" : row
      const columnName = row === "dividend" ? "配当額" : "配当性向"
      const yUnit = row === "dividend" ? currencyToSymbol(dividendCurrency) : "%"
      return {
        key: row,
        graphType: "bar",
        x_unit: "年",
        y_unit: yUnit,
        name: columnName,
        x: filteredData.map((d) => d.fiscal_year),
        y: filteredData.map((d) => d[column]),
      }
    })
  }

  // ラベル設定ボタン（「自動 / 表示 / 非表示」など）の変更ハンドラ
  const handleLabelSettingClick = (newState) => {
    setLabelState(newState)
  }

  // 「設定を保存」ボタン
  const onSaveClick = async () => {
    const newSettings = [
      {
        setting_name: "dashboard_dividend_data_graph_timerange",
        description: selectedTimeRange,
      },
      {
        setting_name: "dashboard_dividend_data_graph_label",
        description: labelState,
      },
    ]
    for (let i = 0; i < newSettings.length; i++) {
      await addUserSetting(newSettings[i])
    }
  }

  // データ・通貨が揃うまでローディング表示
  if (dividendData === null || dividendCurrency === null) {
    return <Loading />
  }

  // 表示用のデータを作成
  const displayData = makeDisplayData(dividendData)
  const chartData = makeChartData(displayData)

  return (
    <div className="tabcontent">
      {displayData.length > 0 ? (
        <>
          <div className="dashboard-row" style={{ marginTop: 0 }}>
            <DividendTable
              dividendData={dividendData}
              dividendCurrency={dividendCurrency}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          </div>
          <div className="dashboard-row" ref={ref}>
            <div className="flex flex-wrap gap-4 mb-4">
              <TimeRangeContainer
                timeRanges={[
                  { label: "3Y", value: "3Y" },
                  { label: "5Y", value: "5Y" },
                  { label: "10Y", value: "10Y" },
                  { label: "MAX", value: "MAX" },
                ]}
                handleTimeRangeSetting={setSelectedTimeRange}
                selectedRange={selectedTimeRange}
              />
              <AxisSettingButton handleLabelSettingClick={handleLabelSettingClick} labelState={labelState} />
              <SaveSettingButton onSaveClick={onSaveClick} />
            </div>
            <ManualBarGraphWrapper initialData={chartData} width={width} showLabel={labelState} />
          </div>
        </>
      ) : (
        <div className="dashboard-row">配当情報なし</div>
      )}
    </div>
  )
}

export default Dividends
